/**
 * Live2DViewer — PixiJS scene that loads and drives a Live2D Cubism 4 avatar.
 *
 * Prerequisites:
 *   The Cubism 4 Core must be loaded in the page BEFORE this module runs:
 *   <script src="https://cubism.live2d.com/sdk-web/cubismcore/live2dcubismcore.min.js"></script>
 *
 * Tracking data arrives from the bridge in Unity's left-handed coordinate
 * system.  Head orientation is derived from the Head (or Neck) bone quaternion
 * and converted to Live2D's AngleX / AngleY / AngleZ parameters.
 *
 * VMC blend-shape names (VRM 0.x / VSeeFace style) and ARKit names (Warudo)
 * are both mapped to standard Cubism 4 parameter IDs.  Custom models may use
 * different parameter IDs — unrecognised names are silently ignored.
 *
 * API is intentionally parallel to VRMViewer so room-viewer.js can treat
 * both classes uniformly.
 */

import { Application }  from 'pixi.js'
import { Live2DModel }  from 'pixi-live2d-display/cubism4'

// ── Maths helpers ─────────────────────────────────────────────────────────────

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)) }

/**
 * Convert a right-handed quaternion to YXZ Euler angles (radians).
 * Returns [pitchX, yawY, rollZ].
 * Matches THREE.Euler.setFromQuaternion(q, 'YXZ') without the Three.js import.
 *
 * @param {number} qx
 * @param {number} qy
 * @param {number} qz
 * @param {number} qw
 * @returns {[number, number, number]}  [pitch, yaw, roll]
 */
function quatToEulerYXZ(qx, qy, qz, qw) {
  const x2 = qx + qx, y2 = qy + qy, z2 = qz + qz
  const xx = qx * x2, xy = qx * y2, xz = qx * z2
  const yy = qy * y2, yz = qy * z2, zz = qz * z2
  const wx = qw * x2, wy = qw * y2, wz = qw * z2

  const m11 = 1 - (yy + zz)
  const m13 = xz + wy
  const m21 = xy + wz
  const m22 = 1 - (xx + zz)
  const m23 = yz - wx
  const m31 = xz - wy
  const m33 = 1 - (xx + yy)

  const sinX = -m23
  const pitch = Math.abs(sinX) < 0.9999999
    ? Math.asin(clamp(sinX, -1, 1))
    : Math.sign(sinX) * (Math.PI / 2)
  const yaw = Math.abs(sinX) < 0.9999999
    ? Math.atan2(m13, m33)
    : Math.atan2(-m31, m11)
  const roll = Math.abs(sinX) < 0.9999999
    ? Math.atan2(m21, m22)
    : 0

  return [pitch, yaw, roll]
}

// ── Live2DViewer ──────────────────────────────────────────────────────────────

export class Live2DViewer {
  /** @type {import('pixi.js').Application} */
  #app
  /** @type {import('pixi-live2d-display').Live2DModel|null} */
  #model = null
  #latestTracking = null
  /** EMA-smoothed values keyed by VMC/ARKit shape name. */
  #smoothBlend = new Map()
  /** EMA alpha — 0=frozen, 1=instant. */
  #blendAlpha = 0.4
  #fpsCallback = null
  #frameCount  = 0
  #fpsTimer

  /**
   * @param {HTMLCanvasElement} canvas
   * @param {{ enableControls?: boolean }} [_opts]  kept for API parity with VRMViewer
   */
  constructor(canvas, _opts = {}) {
    this.#app = new Application({
      view:            canvas,
      backgroundAlpha: 0,      // transparent by default (same as viewer.html usage)
      autoDensity:     true,
      resolution:      Math.min(window.devicePixelRatio, 2),
      autoStart:       false,
    })

    // Resize handling — mirrors VRMViewer's ResizeObserver approach
    const ro = new ResizeObserver(() => this.#resize(canvas))
    ro.observe(canvas.parentElement ?? document.body)
    this.#resize(canvas)

    // FPS counter: fires callback once per second
    this.#fpsTimer = setInterval(() => {
      this.#fpsCallback?.(this.#frameCount)
      this.#frameCount = 0
    }, 1000)

    // Drive tracking + frame count via PixiJS ticker
    this.#app.ticker.add(() => {
      this.#applyTracking()
      this.#frameCount++
    })
    this.#app.ticker.start()
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  /**
   * Load a Live2D Cubism 4 model from a .model3.json URL.
   * @param {string} url
   * @param {(pct: number, phase: string) => void} [onProgress]
   */
  async loadModel(url, onProgress) {
    // Dispose the old model before loading
    if (this.#model) {
      this.#app.stage.removeChild(this.#model)
      this.#model.destroy()
      this.#model = null
    }

    onProgress?.(0, 'Loading')
    const model = await Live2DModel.from(url, {
      autoInteract: false,   // disable built-in mouse/touch interaction
    })
    onProgress?.(100, 'Parsing')

    this.#app.stage.addChild(model)
    this.#model = model
    this.#fitModel()
    return model
  }

  /**
   * Queue a tracking frame to be applied on the next PixiJS tick.
   * @param {object} data  Parsed tracking JSON from the relay server
   */
  applyTracking(data) {
    this.#latestTracking = data
  }

  /**
   * Change the canvas background.
   * @param {string} value  CSS colour string or 'transparent'
   */
  setBackground(value) {
    if (value === 'transparent') {
      this.#app.renderer.background.alpha = 0
    } else {
      this.#app.renderer.background.alpha = 1
      // PixiJS expects a hex number; parse the CSS colour string
      const tmp = new Option()
      tmp.style.color = value
      // Browsers normalise to rgb(...) — parse it
      const rgb = getComputedStyle(tmp).color.match(/\d+/g)
      if (rgb) {
        const hex = (parseInt(rgb[0]) << 16) | (parseInt(rgb[1]) << 8) | parseInt(rgb[2])
        this.#app.renderer.background.color = hex
      }
    }
  }

  /** Register a callback that fires each second with the current FPS. */
  onFPS(cb) { this.#fpsCallback = cb }

  /**
   * Set the expression smoothing amount.
   * @param {number} alpha  0=fully smoothed (frozen), 1=instant (no smoothing)
   */
  setBlendSmoothing(alpha) { this.#blendAlpha = Math.max(0, Math.min(1, alpha)) }

  dispose() {
    clearInterval(this.#fpsTimer)
    this.#app.destroy(false, { children: true })
  }

  // ── Private ─────────────────────────────────────────────────────────────────

  #resize(canvas) {
    const w = canvas.clientWidth  || canvas.parentElement?.clientWidth  || 800
    const h = canvas.clientHeight || canvas.parentElement?.clientHeight || 600
    this.#app.renderer.resize(w, h)
    this.#fitModel()
  }

  /** Scale and centre the model inside the canvas. */
  #fitModel() {
    const model = this.#model
    if (!model) return
    const w = this.#app.renderer.width
    const h = this.#app.renderer.height
    // 0.9 gives a small margin so the model isn't clipped
    const scale = Math.min(w / model.width, h / model.height) * 0.9
    model.scale.set(scale)
    model.x = (w - model.width  * scale) / 2
    model.y = (h - model.height * scale) / 2
  }

  /** EMA-smooth a raw value and return the smoothed result. */
  #smooth(name, raw) {
    const prev = this.#smoothBlend.get(name) ?? raw
    const s    = prev + this.#blendAlpha * (raw - prev)
    this.#smoothBlend.set(name, s)
    return s
  }

  /**
   * Apply the queued tracking frame to the Live2D model's parameters.
   *
   * Standard Cubism 4 parameter IDs are used.  Models with non-standard IDs
   * will still render but those specific parameters won't be driven.
   */
  #applyTracking() {
    const data  = this.#latestTracking
    const model = this.#model
    if (!data || !model) return
    this.#latestTracking = null

    const core = model.internalModel?.coreModel
    if (!core) return

    // Safe parameter setter — silently ignores unknown IDs
    const set = (id, v) => { try { core.setParameterValueById(id, v) } catch {} }

    // ── Head orientation ─────────────────────────────────────────────────────
    // Prefer Head bone; fall back to Neck.
    const headBone = data.bones?.Head ?? data.bones?.Neck
    if (headBone) {
      const b = headBone
      // Unity left-handed → Three.js right-handed: negate qx
      const [pitch, yaw, roll] = quatToEulerYXZ(-b.qx, b.qy, b.qz, b.qw)
      const DEG = 180 / Math.PI

      // Live2D: AngleX positive = turning right, AngleY positive = looking up
      set('ParamAngleX',  clamp( yaw   * DEG, -30, 30))
      set('ParamAngleY',  clamp(-pitch * DEG, -30, 30))
      set('ParamAngleZ',  clamp(-roll  * DEG, -30, 30))

      // Body follows head at reduced amplitude (natural sway)
      set('ParamBodyAngleX', clamp( yaw   * DEG * 0.25, -10, 10))
      set('ParamBodyAngleZ', clamp(-roll  * DEG * 0.25, -10, 10))
    }

    const bs = data.blendShapes ?? {}

    // ── Eye blink ────────────────────────────────────────────────────────────
    // VMC 0.x (VSeeFace / Warudo VRM 0.x)
    const blinkL = this.#smooth('Blink_L', bs.Blink_L ?? bs.blinkLeft ?? 0)
    const blinkR = this.#smooth('Blink_R', bs.Blink_R ?? bs.blinkRight ?? 0)
    set('ParamEyeLOpen', 1 - blinkL)
    set('ParamEyeROpen', 1 - blinkR)
    // ARKit (Warudo ARKit tracker) — override above if present
    if (bs.eyeBlinkLeft  !== undefined) set('ParamEyeLOpen', 1 - this.#smooth('eyeBlinkLeft',  bs.eyeBlinkLeft))
    if (bs.eyeBlinkRight !== undefined) set('ParamEyeROpen', 1 - this.#smooth('eyeBlinkRight', bs.eyeBlinkRight))

    // ── Eye gaze ─────────────────────────────────────────────────────────────
    // Combine look-left/right and look-up/down into a single ±1 range each axis
    const lookR = this.#smooth('LookRight', bs.LookRight ?? bs.lookRight ?? 0)
    const lookL = this.#smooth('LookLeft',  bs.LookLeft  ?? bs.lookLeft  ?? 0)
    const lookU = this.#smooth('LookUp',    bs.LookUp    ?? bs.lookUp    ?? 0)
    const lookD = this.#smooth('LookDown',  bs.LookDown  ?? bs.lookDown  ?? 0)
    set('ParamEyeBallX', lookR - lookL)   // +1 = right, −1 = left
    set('ParamEyeBallY', lookU - lookD)   // +1 = up,   −1 = down

    // ── Mouth ────────────────────────────────────────────────────────────────
    // Open amount (vowel A)
    const mouthOpen = this.#smooth('A', bs.A ?? bs.aa ?? 0)
    set('ParamMouthOpenY', mouthOpen)
    // Mouth shape: positive = smile (Joy/happy), negative = frown (Sorrow/sad)
    const joy    = this.#smooth('Joy',    bs.Joy    ?? bs.happy   ?? 0)
    const sorrow = this.#smooth('Sorrow', bs.Sorrow ?? bs.sad     ?? 0)
    set('ParamMouthForm', joy - sorrow)
    // ARKit smile (average L + R)
    if (bs.mouthSmileLeft !== undefined || bs.mouthSmileRight !== undefined) {
      const sl = this.#smooth('mouthSmileLeft',  bs.mouthSmileLeft  ?? 0)
      const sr = this.#smooth('mouthSmileRight', bs.mouthSmileRight ?? 0)
      set('ParamMouthForm', (sl + sr) * 0.5)
    }
    if (bs.jawOpen !== undefined) {
      set('ParamMouthOpenY', this.#smooth('jawOpen', bs.jawOpen))
    }

    // ── Brows ────────────────────────────────────────────────────────────────
    const angry = this.#smooth('Angry', bs.Angry ?? bs.angry ?? 0)
    set('ParamBrowLY', -angry)
    set('ParamBrowRY', -angry)
    // ARKit brows
    if (bs.browDownLeft !== undefined) {
      set('ParamBrowLY', -this.#smooth('browDownLeft',  bs.browDownLeft))
    }
    if (bs.browDownRight !== undefined) {
      set('ParamBrowRY', -this.#smooth('browDownRight', bs.browDownRight))
    }

    // ── Cheeks (relaxed / Fun) ───────────────────────────────────────────────
    const fun = this.#smooth('Fun', bs.Fun ?? bs.relaxed ?? 0)
    set('ParamCheek', fun)
  }
}
